import styles from "../ClassWizard.module.scss";
import { nameof } from "ts-simple-nameof";
import { useClassTemplateSelection } from "../hooks/useClassTemplateSelection";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { IClassWizardEventTemplate } from "../models/classWizardEventTemplate";
import { getEventType } from "../../helpers/getEventType";
import { useState, useEffect, useRef, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  IClassWizardFormModel,
  NONE_PLAN_TYPES_SENTINEL,
} from "../models/classWizardFormModel";
import { HiddenField } from "../../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { SuggestInputField } from "../../../../../common/components/forms/inputs/InputField/SuggestInputField";
import { DefaultDateField } from "../../../../../common/components/forms/inputs/dateField/DefaultDateField";
import { DefaultFormSelectField } from "../../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormMultiSelectField } from "../../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { TextEditor } from "../../../../../common/components/forms/inputs/textEditor/TextEditor";
import { HeaderTitleSectionWithBackground } from "../../../../../common/components/common/HeaderTitleSectionWithBackground";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { useAppContext } from "../../../../../AppContext";
import {
  CurrencyType,
  PrepaymentProvider,
  UserRole,
} from "../../../../../common/enums";
import { getCurrentUserRole } from "../../../../../common/infrastructure/helpers/userHelper";
import { studioHidesCommissions } from "../../../../../common/infrastructure/helpers/studioHelper";
import { DefaultToggle } from "../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { usePreReservationFormOptions } from "../../../../../common/preReservationForms/usePreReservationFormOptions";
import { CUSTOM_FORM_LINK_OPTION_ID } from "../../../../../common/preReservationForms/constants";
import {
  getAllowOnlineReservationsCopy,
  getInstructorTooltip,
} from "../utils/classWizardOptionsUtility";
import { DurationField } from "../../../../../common/components/forms/inputs/durationField/DurationField";

interface IEventWizardMainInputsProps {
  pageMode: string;
  isIndividualEvent: boolean;
  enableMultisport?: boolean;
  studioInstructors?: ISelectFieldModel[];
  initialData?: IEventWizardInitialData;
  eventTemplates: IClassWizardEventTemplate[] | undefined;
  isLoading: boolean;
}

export const EventWizardMainInputs = ({
  pageMode,
  isIndividualEvent,
  enableMultisport,
  studioInstructors,
  initialData,
  eventTemplates,
  isLoading,
}: IEventWizardMainInputsProps) => {
  const { currentUser, selectedStudio, showGroupTypeOptions } = useAppContext();
  const { setValue } = useFormContext<IClassWizardFormModel>();
  const eventTypeOptions = getEventType();
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<number>();
  const [debouncedIsIndividualEvent, setDebouncedIsIndividualEvent] =
    useState(isIndividualEvent);

  const isGroupInstructor =
    getCurrentUserRole(currentUser) ===
    UserRole.Employee_Instructor_Group_Events;

  useClassTemplateSelection(eventTemplates, selectedSuggestionId);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedIsIndividualEvent(isIndividualEvent);
    }, 200);

    return () => clearTimeout(timeout);
  }, [isIndividualEvent]);

  const forceEmailLabel = debouncedIsIndividualEvent
    ? "Уведоми участникът при добавяне към събитието"
    : "Уведоми всички участници при добавяне към събитието";

  const requirePrepayment = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.requirePrepayment),
  });

  const prepaymentProvider = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.prepaymentProvider),
  });

  const paymentProviderOptions = useMemo(() => {
    const hasStripeKeys = initialData?.hasStripeKeys ?? false;
    return [
      { id: PrepaymentProvider.Link, name: "Линк за плащане", disabled: false },
      {
        id: PrepaymentProvider.Stripe,
        name: hasStripeKeys
          ? "Stripe"
          : "Stripe (първо трябва да бъде настроен от настройките на обекта)",
        disabled: !hasStripeKeys,
      },
    ];
  }, [initialData?.hasStripeKeys]);

  const requireFormCompletion = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.requireFormCompletion),
  });

  const preReservationFormId = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.preReservationFormId),
  });

  const { formOptions, usesPreReservationForms } =
    usePreReservationFormOptions();

  const formCompletionOptions = useMemo(
    () => [
      ...formOptions,
      {
        id: CUSTOM_FORM_LINK_OPTION_ID,
        name: "Персонализиран (външен линк)",
      },
    ],
    [formOptions],
  );

  // When the requirement is switched on in a forms-enabled studio and nothing is
  // selected yet, default to the first in-app form (studios that use forms almost
  // always want one). Only fall back to the custom external-link option when the
  // studio has no forms at all, so the choice is always valid.
  useEffect(() => {
    if (
      requireFormCompletion &&
      usesPreReservationForms &&
      (preReservationFormId === null || preReservationFormId === undefined)
    ) {
      const firstFormId = formOptions[0]?.id ?? CUSTOM_FORM_LINK_OPTION_ID;
      setValue(
        nameof<IClassWizardFormModel>((x) => x.preReservationFormId),
        firstFormId,
      );
    }
  }, [
    requireFormCompletion,
    usesPreReservationForms,
    preReservationFormId,
    formOptions,
    setValue,
  ]);

  const isMultisportAvailable = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.isMultisportAvailable),
  });

  const hasCustomMultisportPrice = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.hasCustomMultisportPrice),
  });

  // Turning the class-level Multisport toggle off makes the custom-price override
  // meaningless, so keep it off and hidden whenever Multisport itself is off.
  useEffect(() => {
    if (!isMultisportAvailable) {
      setValue(
        nameof<IClassWizardFormModel>((x) => x.hasCustomMultisportPrice),
        false,
      );
    }
  }, [isMultisportAvailable, setValue]);

  const showInUserPortal = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.showInUserPortal),
  });

  const willTakePlace = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.willTakePlace),
  });

  const allowOnlineReservationsCopy =
    getAllowOnlineReservationsCopy(showInUserPortal);

  const planTypeIds = useWatch({
    name: nameof<IClassWizardFormModel>((x) => x.planTypeIds),
  });

  const hasActualPlanTypes = planTypeIds?.some((x: number) => x !== 0) ?? false;

  // "Никакви" (None) means no subscription plan may pay for this event, so paying a
  // prepayment with a subscription plan is impossible → force that toggle off and hide it.
  const isNonePlanTypesSelected =
    planTypeIds?.includes(NONE_PLAN_TYPES_SENTINEL) ?? false;
  useEffect(() => {
    if (isNonePlanTypesSelected) {
      setValue("allowSubscriptionPrepayment", false);
    }
  }, [isNonePlanTypesSelected, setValue]);

  const fieldWasShownRef = useRef(false);
  const showPlanTypesField =
    selectedStudio?.studioUsesDifferentPlanTypes ||
    (pageMode === "edit" && hasActualPlanTypes);
  if (showPlanTypesField) {
    fieldWasShownRef.current = true;
  }
  const shouldShowPlanTypesField =
    showPlanTypesField || fieldWasShownRef.current;

  const isAddMode = pageMode === "add";
  const nameInputTooltip = isAddMode
    ? "Когато започнете да пишете, ще се покаже списък с всички класове, които сте създавали досега. Можете да използвате всеки един от шаблон, като кликнете върху него."
    : "";

  return (
    <>
      <HiddenField name={nameof<IClassWizardFormModel>((x) => x.studioId)} />

      <SuggestInputField
        name={nameof<IClassWizardFormModel>((x) => x.name)}
        label="Име на класа"
        placeholder="Спининг за начинаещи"
        tooltip={nameInputTooltip}
        required
        suggestions={isAddMode ? eventTemplates : []}
        onSelect={setSelectedSuggestionId}
      />

      <DefaultDateField
        name={nameof<IClassWizardFormModel>((x) => x.date)}
        label="Дата и час на започване"
        placeholder=""
        showTime
        required
      />

      <DefaultFormSelectField
        name={nameof<IClassWizardFormModel>((x) => x.type)}
        label="Тип на класа"
        options={eventTypeOptions}
        disabled={pageMode === "edit" || isLoading || isGroupInstructor}
        tooltip="• Групов клас: много хора могат да бъдат записани. <br/>• Индивидуален клас: само един потребител може да участва в класа. <br/><br/> Веднъж избран тип на класа не може да бъде променян."
        includeId={false}
      />

      <DurationField
        label="Продължителност"
        name={nameof<IClassWizardFormModel>((x) => x.durationInMinutes)}
        tooltip="Изберете стойност и мерна единица (минути, часове или дни). Класове над 8 часа не могат да бъдат повтарящи се."
        required
      />

      <DefaultInputField
        name={nameof<IClassWizardFormModel>((x) => x.price)}
        label="Цена"
        placeholder="99"
        type="number"
        step={0.01}
        min={0}
        disabled={isLoading}
        tooltip="Базова цена на събитието. Може да се променя при всички начини на плащане, освен при следните случаи:<br/><br/>• Абонаментен план: цена на посещение = цена на плана / брой разрешени посещения<br/>• MultiSport и CoolFit: цена на събитие - комисионната на партньора"
        required
        currency={CurrencyType.EUR}
      />

      {shouldShowPlanTypesField && (
        <DefaultFormMultiSelectField
          name={nameof<IClassWizardFormModel>((x) => x.planTypeIds)}
          disabled={isLoading}
          label="Разрешени абонаментни карти"
          options={initialData?.planTypes}
          tooltip="Типовете планове, с които е позволено да се плаща при потвърждение или плащане за резервация. Изберете „Забрани плащане с абонамент“, за да не се позволява плащане с абонаментен план."
          selectAllOption={{ id: 0, name: "Всички типове" }}
          noneOption={{ id: -1, name: "Забрани плащане с абонамент" }}
        />
      )}

      <RenderWhen condition={!debouncedIsIndividualEvent}>
        <div className={styles.root_numberOfPlacesWrapper}>
          <DefaultInputField
            name={nameof<IClassWizardFormModel>((x) => x.availableSpaces)}
            label="Брой свободни места"
            disabled={isLoading}
            placeholder="15"
            min={0}
            type="number"
            tooltip="Това е максималният (официален) брой на потребители, които класа може да събере. Всеки потребител, който е записан след достигането на този лимит, независимо дали от служител или през портала за потребители, ще бъде маркиран като изчакващ. След което служител може да потвърди участието му."
          />

          <p className={styles.root_numberOfPlacesWrapper_disclaimer}>
            (Ако оставите броя на свободните места да е 0, това ще заложи
            неограничен брой места за класа.)
          </p>
        </div>
      </RenderWhen>

      <RenderWhen condition={!!enableMultisport}>
        <DefaultToggle
          name={nameof<IClassWizardFormModel>((x) => x.isMultisportAvailable)}
          label="Разрешен ли е мултиспорт?"
        />

        <RenderWhen condition={!!isMultisportAvailable}>
          <DefaultToggle
            name={nameof<IClassWizardFormModel>(
              (x) => x.hasCustomMultisportPrice,
            )}
            label="Персонализирана печалба от Multisport за този клас?"
            tooltip="По подразбиране печалбата от Multisport посещение се изчислява от процента, зададен в настройките на обекта. Активирайте това, за да зададете фиксирана сума в евро, която обектът да взима от всяко Multisport посещение само за този клас."
          />

          <RenderWhen condition={!!hasCustomMultisportPrice}>
            <DefaultInputField
              name={nameof<IClassWizardFormModel>(
                (x) => x.multisportCustomPrice,
              )}
              label="Печалба от Multisport посещение"
              placeholder="8"
              type="number"
              step={0.01}
              min={0}
              disabled={isLoading}
              tooltip="Сумата в евро, която обектът взима от всяко посещение с Multisport за този клас, вместо да я изчислява от процента в настройките на обекта."
              required
              currency={CurrencyType.EUR}
            />
          </RenderWhen>
        </RenderWhen>
      </RenderWhen>

      <DefaultFormSelectField
        label="Инструктор"
        name={nameof<IClassWizardFormModel>((x) => x.employeeId)}
        options={studioInstructors}
        disabled={isLoading}
        tooltip={getInstructorTooltip(studioHidesCommissions(selectedStudio))}
        emptyOptionLabel="Без инструктор"
        includeEmptyOption
      />

      <RenderWhen condition={!!debouncedIsIndividualEvent}>
        <DefaultFormSelectField
          label="Потребител"
          disabled={isLoading}
          name={nameof<IClassWizardFormModel>((x) => x.individualUserId)}
          options={initialData?.allAvailableUsers}
          tooltip="Потребителят, който ще участва в това индивидуално събитие."
          required
        />
      </RenderWhen>

      <RenderWhen condition={!!initialData?.studioSettings.usesHalls}>
        <DefaultFormSelectField
          name={nameof<IClassWizardFormModel>((x) => x.hallId)}
          options={initialData?.halls}
          disabled={isLoading}
          label="Зала"
          emptyOptionLabel="Няма избрана зала"
          tooltip="При отваряне на детайлите за клас, потребителят вижда името на обекта, в което той ще се проведе. Ако е избрана зала, вижда и залата, в която ще се проведе."
          includeEmptyOption
        />
      </RenderWhen>

      {/* Outside the group-only "Допълнителни настройки": individual events can be remote too. */}
      <DefaultToggle
        name={nameof<IClassWizardFormModel>((x) => x.isRemote)}
        label="Дистанционна среща (Google Meet)"
        tooltip="Линкът се създава от Google акаунта на собственика (Моят профил)."
      />

      <RenderWhen
        condition={
          !debouncedIsIndividualEvent &&
          showGroupTypeOptions &&
          !!selectedStudio?.useGroupClassCategories
        }
      >
        <DefaultFormSelectField
          name={nameof<IClassWizardFormModel>((x) => x.eventTypeCategoryId)}
          options={initialData?.eventTypeCategories}
          disabled={isLoading}
          label="Категория"
          emptyOptionLabel="Няма избрана категория (отива в 'Други')"
          tooltip="Категорията, в която потребителите виждат класа при резервация по категории."
          includeEmptyOption
        />
      </RenderWhen>

      <RenderWhen condition={!debouncedIsIndividualEvent}>
        <TextEditor
          name={nameof<IClassWizardFormModel>((x) => x.description)}
          label="Описание"
          tooltip="Вижда се от потребителите, в портала за потребители."
          disabled={isLoading}
        />

        <HeaderTitleSectionWithBackground
          text={"Допълнителни настройки"}
          dataOnboarding="event-wizard-extra-settings"
        />

        <DefaultToggle
          name={nameof<IClassWizardFormModel>((x) => x.willTakePlace)}
          tooltip="Когато тази опция е изключена, класът се маркира като отменен: показва се зачертан в календара на потребителите и нови резервации не са възможни. Внимание: Всички текущи резервации на клиенти се отменят автоматично."
          label="Ще се проведе ли класа?"
        />

        <RenderWhen condition={willTakePlace}>
          <DefaultToggle
            name={nameof<IClassWizardFormModel>((x) => x.showInUserPortal)}
            tooltip="Ако го виждат в календара си, ще могат да правят и резервации за него."
            label="Този клас да се вижда ли в календара на потребителите?"
          />

          {/* Shown for hidden classes too: a hidden class is still reachable through a
              copied link, so its owner must always be able to see and change whether
              those visitors can book. Hiding the toggle used to strand a class whose
              flag was already off with no way to turn it back on. */}
          <DefaultToggle
            name={nameof<IClassWizardFormModel>(
              (x) => x.allowOnlineReservations,
            )}
            tooltip={allowOnlineReservationsCopy.tooltip}
            label={allowOnlineReservationsCopy.label}
          />

          <DefaultToggle
            name={nameof<IClassWizardFormModel>((x) => x.allowMusicSuggestions)}
            tooltip="Позволява на потребителите да предлагат музика за класа. Те могат да търсят песни от Spotify и да ги добавят към плейлист, който може да използвате по време на класа."
            label="Позволи музикални предложения"
          />

          <DefaultToggle
            name={nameof<IClassWizardFormModel>((x) => x.requirePrepayment)}
            label="Задължи потребителя да заплати посещението предварително"
            tooltip="Когато тази опция е активирана, потребителите ще трябва да платят предварително чрез линка, който предоставяте (например MyPos, Stripe, Revolut). Новите резервации ще бъдат със статус 'Изчакващ'. Потребителите ще получат имейл с линка за плащане и ще бъдат помолени да добавят номера на резервацията в описанието на плащането. След като платят, вие като администратор трябва да намерите резервацията и да я маркирате като потвърдена и платена."
          />

          <RenderWhen condition={requirePrepayment}>
            <RenderWhen condition={!isNonePlanTypesSelected}>
              <DefaultToggle
                name={nameof<IClassWizardFormModel>(
                  (x) => x.allowSubscriptionPrepayment,
                )}
                label="Позволи плащане с активен абонаментен план"
                tooltip="Когато е активирано, потребителите с активен абонаментен план ще видят опция да заплатят посещението директно от своя план, вместо да преминават през онлайн плащане. Активният план ще се избере автоматично и едно посещение ще бъде приспаднато."
              />
            </RenderWhen>
            <DefaultFormSelectField
              name={nameof<IClassWizardFormModel>((x) => x.prepaymentProvider)}
              label="Начин на плащане"
              options={paymentProviderOptions}
              disabled={isLoading}
              filter={false}
              includeId={false}
              optionDisabled="disabled"
              required
            />
          </RenderWhen>

          <RenderWhen
            condition={
              requirePrepayment &&
              prepaymentProvider === PrepaymentProvider.Link
            }
          >
            <DefaultInputField
              name={nameof<IClassWizardFormModel>((x) => x.prepaymentLink)}
              label="Линк за плащане"
              placeholder="https://example.com/payment"
              disabled={isLoading}
              tooltip="Въведете линка към страницата за плащане. Този линк ще бъде изпратен на потребителите в имейл уведомление."
              prefix="https://"
              required
            />
          </RenderWhen>

          <DefaultToggle
            name={nameof<IClassWizardFormModel>((x) => x.requireFormCompletion)}
            label="Задължи потребителя да попълни формуляр преди събитието"
            tooltip="Когато е активирана, потребителят трябва да попълни формуляр преди резервация. Ако обектът използва вътрешни форми, изберете такава форма (попълва се на място и е задължителна преди резервацията) или „Персонализиран (външен линк)“, който се изпраща с имейл след резервацията."
          />

          <RenderWhen
            condition={requireFormCompletion && usesPreReservationForms}
          >
            <DefaultFormSelectField
              name={nameof<IClassWizardFormModel>(
                (x) => x.preReservationFormId,
              )}
              label="Формуляр"
              options={formCompletionOptions}
              includeId={false}
              tooltip="Изберете вътрешна форма, която потребителят попълва и приема преди резервация, или „Персонализиран (външен линк)“."
            />
          </RenderWhen>

          <RenderWhen
            condition={
              requireFormCompletion &&
              (!usesPreReservationForms ||
                preReservationFormId === CUSTOM_FORM_LINK_OPTION_ID)
            }
          >
            <DefaultInputField
              name={nameof<IClassWizardFormModel>((x) => x.formCompletionLink)}
              label="Линк към формуляра"
              placeholder="https://example.com/form"
              disabled={isLoading}
              tooltip="Въведете линка към формуляра, който потребителите трябва да попълнят (например Google Forms, Typeform, и т.н.). Този линк ще бъде изпратен на потребителите в отделно имейл уведомление."
              prefix="https://"
              required
            />
          </RenderWhen>
        </RenderWhen>
      </RenderWhen>

      <RenderWhen condition={pageMode === "add"}>
        <DefaultToggle
          name={nameof<IClassWizardFormModel>((x) => x.forceEmailNotifications)}
          label={forceEmailLabel}
          tooltip={
            "Тази настройка ще изпрати имейл уведомление до всички добавени потребители, независимо дали са дали съгласие да получават съобщения. Използвай я само когато е наложително."
          }
        />
      </RenderWhen>
    </>
  );
};
